const ZERO_DECIMAL = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga',
  'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf',
]);
const THREE_DECIMAL = new Set(['bhd', 'jod', 'kwd', 'omr', 'tnd']);

export function decimals(currency: string): number {
  const c = currency.toLowerCase();
  if (ZERO_DECIMAL.has(c)) return 0;
  if (THREE_DECIMAL.has(c)) return 3;
  return 2;
}

/**
 * Currencies written after the amount.
 *
 * Symbol placement belongs to the currency, not to the reader's language. A
 * Bulgarian locale renders euros as `39,00 €`, but the euro sign goes in front
 * of the number everywhere the euro is actually used, and a customer looking at
 * a checkout page reads the symbol first. So the position is decided here and
 * only the digit grouping follows the locale.
 *
 * The ones listed below are the opposite case - their own convention is to
 * trail the amount, so `1 234,00 kr` is correct and `kr1 234,00` is not.
 */
const SUFFIX_CURRENCY = new Set([
  'sek', 'nok', 'dkk', 'isk', 'pln', 'czk', 'huf', 'ron', 'bgn',
  'hrk', 'rsd', 'rub', 'uah', 'vnd', 'kzt', 'try',
]);

function symbolPart(currency: string, locale: string, display: 'narrowSymbol' | 'symbol'): string {
  try {
    const part = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      currencyDisplay: display,
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? currency.toUpperCase();
  } catch {
    return currency.toUpperCase();
  }
}

/**
 * The currency's symbol on its own, e.g. `$`, `CA$`, `€`, `лв.`.
 *
 * The reader's own locale gets first say, so a Bulgarian sees `лв.` for leva
 * rather than `BGN`. The exception is the dollar family: most locales narrow
 * CAD, AUD, SGD and the rest to a bare `$`, which on a platform holding several
 * merchants' money in several currencies is not a cosmetic problem - two
 * amounts that are not the same amount would read identically. In that case
 * fall back to the international forms (`CA$`, `A$`), which stay unambiguous.
 */
function symbolFor(currency: string, locale: string): string {
  const code = currency.toUpperCase();
  const local = symbolPart(code, locale, 'narrowSymbol');
  if (local === '$' && code !== 'USD') return symbolPart(code, 'en-US', 'symbol');
  return local;
}

/** Amounts arrive from the API in minor units and are never floats in transit. */
export function money(amount: number, currency: string, locale = 'en-US'): string {
  const places = decimals(currency);
  const digits = new Intl.NumberFormat(locale, {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  }).format(amount / 10 ** places);

  const symbol = symbolFor(currency, locale);
  // A non-breaking space, so an amount never wraps away from its symbol.
  return SUFFIX_CURRENCY.has(currency.toLowerCase())
    ? `${digits} ${symbol}`
    : `${symbol}${digits}`;
}

export function dateTime(iso: string | null, locale = 'en-US'): string {
  if (!iso) return '-';
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

/**
 * "преди 5 мин", "vor 5 Min.", "5 min ago" - whichever the locale calls for.
 *
 * Intl.RelativeTimeFormat is used rather than a hand-written table because
 * Slavic plural rules alone need three forms, and getting them wrong in a
 * console someone reads all day is worse than the dependency.
 */
export function relative(iso: string | null, locale = 'en-US'): string {
  if (!iso) return '-';
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return rtf.format(0, 'second');
  if (mins < 60) return rtf.format(-mins, 'minute');
  const hours = Math.round(mins / 60);
  if (hours < 24) return rtf.format(-hours, 'hour');
  return rtf.format(-Math.round(hours / 24), 'day');
}

/**
 * Long identifiers are truncated in the middle: the prefix says what kind of
 * object it is and the tail is what people actually compare.
 */
export function shortId(id: string, keep = 6): string {
  const [prefix, body] = id.split('_');
  if (!body) return id;
  if (body.length <= keep * 2) return id;
  return `${prefix}_${body.slice(0, keep)}…${body.slice(-keep)}`;
}
